import { forwardRef, memo, useEffect } from 'react';
import i18next from 'i18next';
import 'locales/i18n';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useBooleanState } from 'utils/hooks';
import type { Props as TextFieldProps } from './TextField';
import { TextField } from './TextField';
import { IconButton } from './IconButton';
import { ContentCopy, Subtitles } from '@mui/icons-material';
import { ActionModal } from './ActionModal';
import type { Languages } from 'types/translator';
import { Button } from './Button';

interface TranslationFieldItem {
  id: string;
  language: string;
  value: string;
}

interface Props extends TextFieldProps {
  translatesName: string;
  translationButtonTestId?: string;
}

export const translateValues = (i18next.languages ?? []).map((language) => ({
  language: language as Languages,
  value: '',
}));

export const TranslationField = memo(
  forwardRef<HTMLDivElement, Props>(({ translatesName, label, translationButtonTestId, ...rest }, ref) => {
    const { t } = useTranslation();
    const [modal, toggleModal] = useBooleanState(false);
    const { control, setValue, getValues } = useFormContext();
    const { fields, update } = useFieldArray<{ [key: string]: TranslationFieldItem[] }>({ control, name: translatesName });

    const handleCopyAll = () => {
      const sourceValue = getValues(rest.name!) as string;
      if (sourceValue) {
        fields.forEach((field, index) => {
          update(index, { ...field, value: sourceValue });
        });
      }
    };

    useEffect(() => {
      const languagesCount = i18next.languages?.length || 0;
      if (fields.length < languagesCount) {
        const currentValues = getValues(translatesName) || [];
        const updatedValues = i18next.languages.map((lang) => {
          const existing = currentValues.find((v: TranslationFieldItem) => v.language === lang);
          return existing ? { ...existing } : { language: lang, value: '' };
        });
        setValue(translatesName, updatedValues);
      }
    }, [fields.length, translatesName, setValue]);

    return (
      <div ref={ref} style={{ width: rest.fullWidth ? '100%' : undefined }}>
        <TextField
          label={label}
          {...rest}
          slotProps={{
            input: {
              endAdornment: (
                <IconButton data-testid={translationButtonTestId} onClick={toggleModal} size={15}>
                  <Subtitles fontSize="small" />
                </IconButton>
              ),
            },
          }}
        />
        <ActionModal title={label} open={modal} onClose={toggleModal} onCancel={toggleModal} cancelText={t('common.close')}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <Button
              variant="text"
              size="small"
              onClick={handleCopyAll}
              startIcon={<ContentCopy />}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#707EAE' }}>
              {t('common.copyFromDefault')}
            </Button>
          </div>
          {fields.map((item, index) => (
            <Controller
              key={item.id}
              control={control}
              name={`${translatesName}[${index}].value`}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label={t(`languages.${item.language}`)}
                  {...field}
                  error={Boolean(error)}
                  helperText={error?.message}
                  fullWidth
                  background="white"
                  sx={{ marginBottom: '16px' }}
                />
              )}
            />
          ))}
        </ActionModal>
      </div>
    );
  })
);
